/**
 * This file is called in the main react react entry point
 */
import React from "react"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import SoundBank, { SoundBankLoader } from "aldoo-ra/SoundBank"
import { AldooAPI, configureEncryption } from "aldoo-ra/API"
import { AldooRealtime } from "aldoo-ra/Realtime"
import PlatformDetector from "aldoo-ra/PlatformDetector"
import AdminSiteUpdateScan from "aldoo-ra/Admin/Updates/Deployment/admin-site-update-scan"
import config from "aldoo-ra/Config"
import "aldoo-ra/SiteTheme" // Import main SiteTheme module to trigger auto-initialization
import isAdmin from "./isAdmin"
import PlatformManager from "aldoo-ra/PlatformManager"
import { BrowserThemeDetector } from "aldoo-ra/SiteTheme/BrowserThemeDetector"
import ScrollToHash from "aldoo-ra/ScrollToHash"
import EnsureStableLogin from "aldoo-ra/EnsureStableLogin"
import NotificationProvider from "aldoo-ra/Notification"
import MessageDialogProvider from "aldoo-ra/MessageDialog"
import PromptDialogProvider from "aldoo-ra/PromptDialog"
import GTMTracker from "aldoo-ra/GTMTracker"
import GoogleTracking from "aldoo-ra/Tracking/google"
import FacebookTracking from "aldoo-ra/Tracking/facebook"
import MicrosoftTracking from "aldoo-ra/Tracking/microsoft"
import PageViewTracker from "aldoo-ra/Tracking/PageViewTracker"
import Signin from "aldoo-ra/Signin"
import { ToastContainer } from "aldoo-ra/Toast"
import SessionMonitor from "aldoo-ra/SessionMonitor"
import LiveEdit from "aldoo-ra/LiveEdit"

const AldooBoot = () => {
  //Initialize the platform auth system
  PlatformAuth.useAuth()
  AldooAPI()
  AldooRealtime()
  //use profile support
  //this will listen for credential changes and update the user profile automatically
  PlatformAuth.useProfile()
  //seamless login from a one-time token (?otp= from the mobile app)
  PlatformAuth.useOTPLogin()
  //capture marketing attribution (utm_* / click ids) → user.attribution
  PlatformAuth.useUTMCapture()

  // Initialize services once on first mount
  React.useEffect(() => {
    // Configure API encryption from deployment config
    if (config.ENCRYPTION_ENABLED || config.INFLIGHT_ENCRYPTION_KEY) {
      configureEncryption({
        enabled: config.ENCRYPTION_ENABLED || false,
        key: config.INFLIGHT_ENCRYPTION_KEY
      })
    }

    // Load project sound map if it exists. Use import.meta.glob so the
    // import is resolved at build time against the actual project layout;
    // when the file is absent the glob returns {} and we silently skip.
    const soundMapModules = import.meta.glob('/components/Audio/SoundMap.{js,jsx,ts,tsx}')
    const soundMapKey = Object.keys(soundMapModules)[0]
    if (soundMapKey) {
      soundMapModules[soundMapKey]()
        .then((m) => {
          const { default: map, soundBankId } = m
          SoundBank.registerSoundMap(soundBankId, map)
        })
        .catch(() => {})
    }

    // Admin-only modules - only load when in admin context
    if (isAdmin()) {
      //Initialize CMS Models and database types for admin
      import("aldoo-ra/CMSModels").then(module => {
        const CMSModelsLoader = module.default
        CMSModelsLoader()
      })
    }
  }, [])

  return (
    <>
      <PlatformDetector />
      <AdminSiteUpdateScan />
      <PlatformManager />
      <BrowserThemeDetector />
      <ScrollToHash />
      <EnsureStableLogin />
      <SessionMonitor />
      <NotificationProvider />
      <MessageDialogProvider />
      <PromptDialogProvider />
      <GTMTracker />
      <GoogleTracking />
      <FacebookTracking />
      <MicrosoftTracking />
      <PageViewTracker />
      <Signin />
      <ToastContainer />
      <SoundBankLoader />
      <LiveEdit />
    </>
  )
}

export default AldooBoot
